import { z } from 'zod';

import { mongoIdSchema } from './shared';

export enum SemanticTermStatus {
  Passed = 'aPassed',
  NeedsImprovement = 'bNeedsImprovement',
  Unused = 'dUnused',
}

export enum ScrapingStatus {
  Pending = 'pending',
  Completed = 'completed',
}
export enum ScrapingProvider {
  Serp = 'serp',
  Anthropic = 'anthropic',
  Google = 'google',
  Openai = 'openai',
  Perplexity = 'perplexity',
}

export enum ScrapingMessage {
  PreprocessingData = 'Preprocessing Data',
  AnthropicAnswered = 'Anthropic answered',
  GoogleAnswered = 'Google answered',
  OpenaiAnswered = 'OpenAI answered',
  PerplexityAnswered = 'Perplexity answered',
}

export enum AnalyzingStatus {
  Pending = 'pending',
  Completed = 'completed',
}

export enum AnalyzingMessage {
  ResultsAggregated = 'Results aggregated',
  Success = 'Success',
}

export const contentAnalysisSemanticTerm = z.object({
  termScore: z.number(),
  term: z.string(),
  averageUsage: z.number(),
  usedInHeadings: z.boolean(),
  usageRange: z.tuple([z.number(), z.number()]), // [1, 3]

  // internal data. not from the ai team
  isHidden: z.boolean().nullish(),
  occurrencesCount: z.number().nullish(),
  status: z
    .enum([
      SemanticTermStatus.Passed,
      SemanticTermStatus.NeedsImprovement,
      SemanticTermStatus.Unused,
    ])
    .nullish(),
});

export const contentAnalysisLLMResponseSchema = z.object({
  provider: z.string(),
  model: z.string(),
  text: z.string().nullish(),
  source: z.array(z.object({ title: z.string(), url: z.string() })),
});

export type ContentAnalysisLLMResponse = z.infer<typeof contentAnalysisLLMResponseSchema>;

export type ContentAnalysisSemanticTerm = z.infer<typeof contentAnalysisSemanticTerm>;

export const contentAnalysisGeoSchema = z.object({
  aggregation: z.string(),
  llmResponses: z.array(contentAnalysisLLMResponseSchema),
  contentGaps: z.string(),
  factualConsensus: z.string(),
  keyMetrics: z.string(),
});

export type ContentAnalysisGeo = z.infer<typeof contentAnalysisGeoSchema>;

export const contentAnalysisCreateSchema = z.object({
  contentId: mongoIdSchema,
  averageHeadingsPerPage: z.number(),
  averageWordsPerPage: z.number(),
  semanticTerms: z.array(contentAnalysisSemanticTerm),

  questions: z.array(z.string()),
  secondaryKeywords: z.array(z.string()),
  userIntent: z.string(),
  pageReports: z.array(
    z
      .object({
        contentLength: z.number(),
        ranking: z.number(),
        seoScore: z.number(),
        content: z.string(),
        metaTitle: z.string(),
        outlineStr: z.string(),
        url: z.string(),
        outlineList: z.array(z.tuple([z.string(), z.string()])).default([]),
      })
      .strip()
      .partial()
  ),
  pageType: z.record(z.number()),
  internalLinks: z
    .array(
      z.object({
        metaTitle: z.string(),
        metaDescription: z.string(),
        url: z.string(),
      })
    )
    .nullish()
    .default([]),
  selectLinksResults: z.any(), // we are not sure of the data that comes in but we dont need it
  geo: contentAnalysisGeoSchema.nullish(),

  scraping: z
    .object({
      [ScrapingProvider.Serp]: z.enum([ScrapingStatus.Pending, ScrapingStatus.Completed]),
      llms: z.record(
        z.enum([
          ScrapingProvider.Anthropic,
          ScrapingProvider.Google,
          ScrapingProvider.Openai,
          ScrapingProvider.Perplexity,
        ]),
        z.enum([ScrapingStatus.Pending, ScrapingStatus.Completed])
      ),
    })
    .nullish(),
  analysing: z
    .object({
      [ScrapingProvider.Serp]: z.enum([AnalyzingStatus.Pending, AnalyzingStatus.Completed]),
      llms: z.enum([AnalyzingStatus.Pending, AnalyzingStatus.Completed]),
    })
    .nullish(),
});
export type ContentAnalysisCreate = z.infer<typeof contentAnalysisCreateSchema>;

export const contentAnalysisSchema = z
  .object({
    _id: mongoIdSchema,
    createdAt: z.number(),
    updatedAt: z.number(),
  })
  .merge(contentAnalysisCreateSchema);

export type ContentAnalysis = z.infer<typeof contentAnalysisSchema>;
